import React from 'react';
import { ArrowRight, BarChart3, BrainCircuit, FileText, CheckCircle2 } from 'lucide-react';
import { SERVICES } from '../data';
import { usePageContext } from '../contexts';

const SERVICE_ICONS = {
  'antitrust-competition': FileText,
  'ai-strategy-consulting': BarChart3,
  'information-strategy-risk-regulation': BrainCircuit,
};

const BulletList = ({ items }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-200 border border-zinc-200">
    {items.map((item) => (
      <div key={item} className="bg-[#fcfaf7] p-5 flex gap-3">
        <CheckCircle2 size={16} className="text-zinc-900 shrink-0 mt-1" />
        <p className="text-zinc-700 leading-relaxed">{item}</p>
      </div>
    ))}
  </div>
);

const Section = ({ eyebrow, title, children }) => (
  <section className="py-16 md:py-24 border-t border-zinc-200">
    <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-8 lg:gap-16">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-400 mb-4">{eyebrow}</p>
        <h2 className="text-3xl md:text-4xl font-bold tracking-tight leading-tight">{title}</h2>
      </div>
      <div>{children}</div>
    </div>
  </section>
);

const ServiceCard = ({ service, index, onClick }) => {
  const Icon = SERVICE_ICONS[service.id] || FileText;

  return (
    <button
      onClick={onClick}
      className="group text-left bg-white border border-zinc-200 hover:border-zinc-900 transition-colors overflow-hidden"
    >
      <div className="aspect-[16/10] overflow-hidden bg-zinc-100">
        <img
          src={service.image}
          alt={service.title}
          className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
        />
      </div>
      <div className="p-6 md:p-8">
        <div className="flex items-center justify-between mb-8">
          <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-400">0{index + 1}</span>
          <div className="w-10 h-10 border border-zinc-200 flex items-center justify-center text-zinc-500 group-hover:bg-zinc-900 group-hover:text-white group-hover:border-zinc-900 transition-colors">
            <Icon size={18} />
          </div>
        </div>
        <h3 className="text-2xl md:text-3xl font-bold tracking-tight mb-4">{service.shortTitle}</h3>
        <p className="text-zinc-500 leading-relaxed mb-8">{service.summary}</p>
        <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-zinc-900">
          Explore service
          <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
        </span>
      </div>
    </button>
  );
};

const ServicesOverview = () => {
  const { navigateToService, navigateTo } = usePageContext();

  return (
    <div className="bg-[#fcfaf7] text-zinc-900">
      <section className="relative min-h-[620px] pt-28 md:pt-36 flex items-end overflow-hidden bg-zinc-950">
        <img
          src="https://images.unsplash.com/photo-1517048676732-d65bc937f952?auto=format&fit=crop&q=80"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-50 grayscale"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/60 to-zinc-950/20" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 pb-16 md:pb-24 w-full">
          <p className="text-[10px] font-bold uppercase tracking-[0.35em] text-orange-300 mb-6">Services</p>
          <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-white leading-[0.9] max-w-5xl">
            Decision infrastructure for companies operating in complex markets.
          </h1>
          <p className="mt-8 text-lg md:text-2xl text-zinc-300 leading-relaxed max-w-3xl">
            Panometra builds the research, models, and AI systems companies need to understand markets, test assumptions, and execute with greater precision.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-[0.8fr_1.2fr] gap-12 items-start mb-16">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-400 mb-4">How we work</p>
            <h2 className="text-4xl md:text-4xl font-bold tracking-tight leading-tight">
              From external signal to internal operating advantage.
            </h2>
          </div>
          <p className="text-xl text-zinc-600 leading-relaxed">
            We combine economic intelligence, scenario modelling, and AI implementation to help leadership teams move from fragmented information to decision systems they can actually run the business with.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {SERVICES.map((service, index) => (
            <ServiceCard
              key={service.id}
              service={service}
              index={index}
              onClick={() => navigateToService(service.id)}
            />
          ))}
        </div>
      </section>

      <section className="bg-zinc-900 text-white">
        <div className="max-w-7xl mx-auto px-6 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-10 items-center">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-500 mb-5">Start with the decision</p>
            <h2 className="text-4xl md:text-6xl font-bold tracking-tight leading-tight max-w-4xl">
              Bring us the market, model, or workflow your team needs to understand.
            </h2>
          </div>
          <button
            onClick={() => navigateTo('apply')}
            className="inline-flex items-center justify-center gap-3 bg-white text-zinc-950 px-8 py-5 text-xs font-bold uppercase tracking-[0.2em] hover:bg-orange-500 hover:text-white transition-colors"
          >
            Work with us
            <ArrowRight size={16} />
          </button>
        </div>
      </section>
    </div>
  );
};

const ServiceDetail = ({ service }) => {
  const { navigateToService, navigateTo } = usePageContext();
  const Icon = SERVICE_ICONS[service.id] || FileText;
  const relatedServices = SERVICES.filter((item) => item.id !== service.id);
  const hiddenSections = new Set(service.hiddenSections || []);

  return (
    <div className="bg-[#fcfaf7] text-zinc-900">
      <section className="relative min-h-[600px] pt-28 md:pt-36 flex items-end overflow-hidden bg-zinc-950">
        <img
          src={service.image}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-45 grayscale"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/70 to-zinc-950/10" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 pb-14 md:pb-20 w-full">
          <button
            onClick={() => navigateToService()}
            className="mb-8 text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-400 hover:text-white transition-colors"
          >
            Services / {service.shortTitle}
          </button>
          <div className="w-12 h-12 border border-white/20 bg-white/10 text-white flex items-center justify-center mb-8">
            <Icon size={22} />
          </div>
          <h1 className="text-5xl md:text-8xl font-bold tracking-tight text-white leading-[0.9] max-w-5xl">
            {service.title}
          </h1>
          <p className="mt-8 text-2xl md:text-3xl text-orange-200 font-medium leading-tight max-w-4xl">
            {service.headline}
          </p>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-12 lg:gap-20 py-16 md:py-24">
          <aside className="lg:sticky lg:top-28 h-fit">
            <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-400 mb-5">Services</p>
            <div className="border border-zinc-200 bg-white">
              {SERVICES.map((item) => (
                <button
                  key={item.id}
                  onClick={() => navigateToService(item.id)}
                  className={`w-full text-left px-5 py-4 border-b last:border-b-0 border-zinc-100 text-sm font-bold transition-colors ${
                    item.id === service.id ? 'bg-zinc-900 text-white' : 'text-zinc-500 hover:text-zinc-900 hover:bg-zinc-50'
                  }`}
                >
                  {item.shortTitle}
                </button>
              ))}
            </div>
          </aside>

          <article>
            <div className="max-w-3xl">
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-400 mb-5">{service.kicker}</p>
              {service.intro.map((paragraph) => (
                <p key={paragraph} className="text-xl md:text-2xl text-zinc-700 leading-relaxed mb-7">
                  {paragraph}
                </p>
              ))}
            </div>

            <Section eyebrow={service.questionsEyebrow || 'Decision agenda'} title={service.questionsTitle || 'Board-level questions we resolve'}>
              <BulletList items={service.questions} />
            </Section>

            {!hiddenSections.has('approach') && (
              <Section eyebrow={service.approachEyebrow || 'Method'} title={service.approachTitle || 'How we build the intelligence layer'}>
                <p className="text-xl text-zinc-600 leading-relaxed mb-8">{service.approachIntro}</p>
                <BulletList items={service.approach} />
              </Section>
            )}

            {!hiddenSections.has('deliverables') && (
              <Section eyebrow={service.deliverablesEyebrow || 'Outputs'} title={service.deliverablesTitle || 'What the engagement produces'}>
                <BulletList items={service.deliverables} />
              </Section>
            )}

            {!hiddenSections.has('useCases') && (
              <Section eyebrow={service.useCasesEyebrow || 'Use cases'} title={service.useCasesTitle || 'Where the work creates leverage'}>
                <BulletList items={service.useCases} />
              </Section>
            )}

            {!hiddenSections.has('applications') && (
              <Section eyebrow="Applications" title={service.applicationsTitle}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {service.applications.map((item) => (
                    <div key={item.title} className="bg-white border border-zinc-200 p-6">
                      <h3 className="font-bold text-xl mb-3">{item.title}</h3>
                      <p className="text-zinc-600 leading-relaxed">{item.description}</p>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {service.formats && !hiddenSections.has('formats') && (
              <Section eyebrow="Formats" title="Engagement formats">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {service.formats.map((item) => (
                    <div key={item.title} className="bg-zinc-900 text-white p-6">
                      <h3 className="font-bold text-xl mb-3">{item.title}</h3>
                      <p className="text-zinc-300 leading-relaxed">{item.description}</p>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {!hiddenSections.has('why') && (
              <Section eyebrow="Why Panometra" title="Strategy, economics, and systems thinking in one team">
                <p className="text-xl text-zinc-700 leading-relaxed">{service.why}</p>
              </Section>
            )}

            <section className="my-16 md:my-24 bg-zinc-900 text-white p-8 md:p-12">
              <p className="text-3xl md:text-5xl font-bold tracking-tight leading-tight max-w-4xl">
                {service.closing}
              </p>
              <button
                onClick={() => navigateTo('apply')}
                className="mt-10 inline-flex items-center gap-3 bg-white text-zinc-950 px-7 py-4 text-xs font-bold uppercase tracking-[0.2em] hover:bg-orange-500 hover:text-white transition-colors"
              >
                Discuss this service
                <ArrowRight size={15} />
              </button>
            </section>

            <section className="pb-20">
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-zinc-400 mb-5">Related services</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {relatedServices.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => navigateToService(item.id)}
                    className="text-left bg-white border border-zinc-200 p-6 hover:border-zinc-900 transition-colors"
                  >
                    <h3 className="text-2xl font-bold mb-3">{item.shortTitle}</h3>
                    <p className="text-zinc-500 leading-relaxed">{item.summary}</p>
                  </button>
                ))}
              </div>
            </section>
          </article>
        </div>
      </div>
    </div>
  );
};

export const ServicesPage = () => {
  const { currentServiceId } = usePageContext();
  const service = SERVICES.find((item) => item.id === currentServiceId);

  if (!service) {
    return <ServicesOverview />;
  }

  return <ServiceDetail service={service} />;
};
